import { z } from 'zod'

/**
 * Role schema with Zod validation
 * Defines all available roles in the system
 */
export const RoleSchema = z.enum([
  'super_user',
  // Platform-tenant service role: permissions == admin (within a tenant) but can switch
  // tenants (impersonation) like super_user. Strictly admin-level — no platform/cross-tenant
  // privileges. Only super_user may assign it, only in the platform tenant.
  'integrator',
  'admin',
  'manager',
  'viewer'
])

/**
 * Permission schema defining all available permissions in the system
 * Format: resource.action (e.g., users.create, voice_agents.view)
 */
export const PermissionSchema = z.enum([
  // User management
  'users.view',
  'users.create',
  'users.edit',
  'users.delete',
  'users.manage_permissions',

  // Tenant management
  'tenants.view',
  'tenants.view_own',
  'tenants.create',
  'tenants.edit_own',
  'tenants.archive',
  'tenants.purge',

  // Tenant settings
  'tenant_settings.view',
  'tenant_settings.edit',
  'tenant_settings.create',
  'tenant_settings.delete',

  // User settings
  'user_settings.view',
  'user_settings.create',
  'user_settings.edit',
  'user_settings.delete',

  // System settings
  'system_settings.view',
  'system_settings.edit',

  // Platform-operator notification routing (System Notifications screen)
  'system_notifications.manage',

  // Voice Agents
  'voice_agents.view',
  'voice_agents.manage',

  // Email Agents
  'email_agents.view',
  'email_agents.manage',
  'email_agents.analytics',

  // Calls
  'calls.view',
  'calls.view_all',
  'calls.delete',

  // Agent Tasks
  'agent_tasks.view',
  'agent_tasks.manage',

  // Email
  'email.view',
  'email.manage',
  'email.sync',
  'email.qa_feedback',
  'email.crm_lead',

  // Integrations
  'integrations.view',
  'integrations.*',
  'integrations.google.manage',
  'integrations.kommo.manage',
  'integrations.shopify.manage',
  'integrations.shopify.delete_data',

  // Delivery operations (separate from integration credential management)
  'deliveries.view',
  'deliveries.reconcile',

  // Shopify (separate from integrations - controls access to Shopify pages)
  'shopify.view',

  // CRM (controls access to CRM features in UI, provider-agnostic)
  'crm.view',

  // Analytics and Financial
  'analytics.view',
  'financial.view',

  // AI
  'ai.view',
  'ai.providers.manage',
  'ai.models.manage',
  'ai.configs.manage',
  'ai.global_configs.manage',
  // Per-agent AI model override (text agents). Super-user-only to grant —
  // see SUPER_USER_ONLY_PERMISSIONS.
  'ai.agent_model_override',
  'ai.logs.view',
  'ai.logs.view_tools',
  'ai.knowledge_base.manage',

  // PBX (Mini-PBX)
  'pbx.view',
  'pbx.manage',

  // Messaging Agents
  'messaging_agents.view',
  'messaging_agents.manage',

  // Notification Channels (tenant-level alerting sinks: email/Telegram)
  'notifications.manage',

  // Tenant API keys
  'api_keys.manage',

  // Billing
  'billing.view',
  'billing.manage',
  'billing.view_all',
  'billing.view_tenants',
  'billing.manage_plans',
  'billing.manage_tenants',
  'billing.grant_credits',
  'billing.manage_elevenlabs_pool',

  // Demo mode (super-user-only — see SUPER_USER_ONLY_PERMISSIONS)
  'demo_mode.manage'
])

/**
 * User authentication data schema
 * Server-derived domain authorization data returned by the cookie/Redis resolver
 */
export const UserAuthDataSchema = z.object({
  user_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  impersonated_tenant_id: z.string().uuid().optional(),
  context_revision: z.number().int().nonnegative().optional(),
  role: RoleSchema,
  permissions: z.array(PermissionSchema),
  /** Explicitly disabled permissions - overrides role and additional permissions, even for super_user */
  disabled_permissions: z.array(PermissionSchema).optional(),
  // Profile claims (v2) — optional for backward compat with old JWTs
  email: z.string().optional(),
  name: z.string().optional(),
  timezone: z.string().optional(),
  uses_dst: z.boolean().optional(),
  /**
   * Display-only: drives the "verify your email" banner in the first paid
   * session. NOT an authorization gate — login/refresh enforce verification
   * against the database. Absent on legacy tokens (treated as verified by the
   * banner, which simply stays hidden).
   */
  email_verified: z.boolean().optional(),
  /**
   * Whether the user's HOME tenant is the platform tenant. Derived server-side
   * by the server resolver from `tenant_id`, so the client can gate
   * platform-only surfaces without knowing PLATFORM_TENANT_ID. Stable under
   * impersonation (keyed off home `tenant_id`, not the active one). Absent on
   * legacy tokens → treated as false. NOT the authorization gate — server
   * endpoints re-check via isPlatformTenant(); this is the UI hint.
   */
  is_platform_tenant: z.boolean().optional(),
})

// TypeScript types inferred from Zod schemas
export type Role = z.infer<typeof RoleSchema>
export type Permission = z.infer<typeof PermissionSchema>
export type UserAuthData = z.infer<typeof UserAuthDataSchema>
